/**
 * Automated Synthesis Settings Component
 * 
 * Allows configuration of the daily clinical synthesis scheduler:
 * - Enable/disable automated synthesis
 * - Set run time (default: 6 AM)
 * - Configure notification channels (email, SMS, both)
 * - View synthesis history and reports
 * - Manual trigger capability
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Separator } from "./ui/separator";
import { ScrollArea } from "./ui/scroll-area";
import { Progress } from "./ui/progress";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Clock,
  Play,
  Pause,
  Mail,
  MessageSquare,
  Bell,
  Settings,
  History,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  Users,
  Activity,
  FileText,
  Zap,
  Eye
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

// Generate time options for scheduler
const TIME_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: hour.toString(),
  label: `${hour.toString().padStart(2, '0')}:00 ${hour < 12 ? 'AM' : 'PM'}`.replace(/^0/, '12 ').replace('12 AM', '12 AM (Midnight)').replace('12 PM', '12 PM (Noon)')
}));

export function AutomatedSynthesisSettings({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [config, setConfig] = useState({
    enabled: true,
    run_hour: 6,
    run_minute: 0,
    notification_channel: "email",
    include_stable_patients: false,
    critical_only: false,
    recipient_roles: ["admin", "rn_case_manager", "md", "coordinator"]
  });
  const [status, setStatus] = useState({
    running: false,
    last_run: null,
    next_scheduled_run: null
  });
  const [history, setHistory] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/clinical-logic/scheduler/config`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/clinical-logic/scheduler/history?limit=10`).catch(() => ({ data: { runs: [] } }))
      ]);
      
      if (configRes.data.config) {
        setConfig(configRes.data.config);
      }
      if (configRes.data.status) {
        setStatus(configRes.data.status);
      }
      setHistory(historyRes.data.runs || []);
    } catch (err) {
      console.error("Failed to fetch scheduler data:", err);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/api/clinical-logic/scheduler/config`, config);
      
      if (response.data.success) {
        toast.success("Scheduler settings saved", {
          description: config.enabled 
            ? `Synthesis will run daily at ${config.run_hour.toString().padStart(2, '0')}:00 UTC`
            : "Automated synthesis disabled"
        });
        fetchData();
      }
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const triggerManualRun = async () => {
    setTriggering(true);
    try {
      const response = await axios.post(`${API_URL}/api/clinical-logic/scheduler/run-now`);
      
      if (response.data.success) {
        toast.success("Clinical synthesis started", {
          description: "Processing all active patients. This may take a few minutes."
        });
        
        // Refresh history after a delay
        setTimeout(fetchData, 5000);
      }
    } catch (err) {
      toast.error("Failed to start synthesis");
    } finally {
      setTriggering(false);
    }
  };

  const viewReport = async (runId) => {
    try {
      const response = await axios.get(`${API_URL}/api/clinical-logic/scheduler/report/${runId}`);
      setSelectedReport(response.data);
      setReportDialogOpen(true);
    } catch (err) {
      toast.error("Failed to load report");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const config = {
      completed: { color: "bg-green-500/20 text-green-300", icon: CheckCircle },
      running: { color: "bg-blue-500/20 text-blue-300", icon: RefreshCw },
      failed: { color: "bg-red-500/20 text-red-300", icon: XCircle },
      partial: { color: "bg-amber-500/20 text-amber-300", icon: AlertTriangle }
    };
    const { color, icon: Icon } = config[status] || config.running;
    return (
      <Badge className={color}>
        <Icon className={`w-3 h-3 mr-1 ${status === 'running' ? 'animate-spin' : ''}`} />
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-teal-400" />
        <span className="ml-2 text-slate-400">Loading scheduler settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-violet-500/20 to-purple-500/20">
            <Calendar className="w-6 h-6 text-violet-400" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Automated Daily Synthesis</h2>
            <p className="text-xs text-slate-400">
              Configure automatic clinical analysis for all patients
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={status.running ? "bg-green-500/20 text-green-300" : "bg-slate-500/20 text-slate-300"}>
            {status.running ? "Scheduler Active" : "Scheduler Inactive"}
          </Badge>
        </div>
      </div>

      {/* Status Overview */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="pt-4">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-slate-400 mb-1">Last Run</p>
              <p className="text-sm font-medium">{formatDate(status.last_run)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Next Scheduled</p>
              <p className="text-sm font-medium text-teal-400">
                {config.enabled ? formatDate(status.next_scheduled_run) : "Disabled"}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Schedule</p>
              <p className="text-sm font-medium">
                {config.run_hour.toString().padStart(2, '0')}:00 UTC Daily
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Settings className="w-4 h-4 text-slate-400" />
            Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Enable Automated Synthesis</Label>
              <p className="text-xs text-slate-500">Run analysis for all patients daily</p>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
            />
          </div>
          
          <Separator className="bg-slate-700" />
          
          {/* Run Time */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Run Time (UTC)</Label>
              <p className="text-xs text-slate-500">When to run daily synthesis</p>
            </div>
            <Select
              value={config.run_hour.toString()}
              onValueChange={(value) => setConfig({ ...config, run_hour: parseInt(value) })}
            >
              <SelectTrigger className="w-[180px]">
                <Clock className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIME_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Separator className="bg-slate-700" />
          
          {/* Notification Channel */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Notification Channel</Label>
              <p className="text-xs text-slate-500">How to notify care team</p>
            </div>
            <Select
              value={config.notification_channel}
              onValueChange={(value) => setConfig({ ...config, notification_channel: value })}
            >
              <SelectTrigger className="w-[180px]">
                <Bell className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Only
                  </div>
                </SelectItem>
                <SelectItem value="sms">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    SMS Only
                  </div>
                </SelectItem>
                <SelectItem value="both">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    Email + SMS
                  </div>
                </SelectItem>
                <SelectItem value="none">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    No Notifications
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Separator className="bg-slate-700" />
          
          {/* Critical Only */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Critical Alerts Only</Label>
              <p className="text-xs text-slate-500">Only notify for critical/active dying patients</p>
            </div>
            <Switch
              checked={config.critical_only}
              onCheckedChange={(checked) => setConfig({ ...config, critical_only: checked })}
            />
          </div>
        </CardContent>
        <CardFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={saveConfig} disabled={saving}>
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Settings
          </Button>
        </CardFooter>
      </Card>

      {/* Manual Trigger */}
      <Card className="bg-slate-800/50 border-slate-700 border-l-4 border-l-teal-500">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Zap className="w-5 h-5 text-teal-400" />
              <div>
                <p className="font-medium">Run Synthesis Now</p>
                <p className="text-xs text-slate-400">Manually trigger clinical analysis for all patients</p>
              </div>
            </div>
            <Button onClick={triggerManualRun} disabled={triggering}>
              {triggering ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Running...</>
              ) : (
                <><Play className="w-4 h-4 mr-2" /> Run Now</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <History className="w-4 h-4 text-slate-400" />
            Synthesis History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-6 text-slate-500">
              <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No synthesis runs yet</p>
            </div>
          ) : (
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {history.map((run) => (
                  <div
                    key={run.run_id}
                    className="flex items-center justify-between p-3 bg-slate-900 rounded border border-slate-700"
                  >
                    <div className="flex items-center gap-3">
                      {getStatusBadge(run.status)}
                      <div>
                        <p className="text-sm">{formatDate(run.started_at)}</p>
                        <p className="text-xs text-slate-500">
                          {run.patients_processed} patients • {run.critical_alerts || 0} critical • {run.urgent_alerts || 0} urgent
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {run.trigger}
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => viewReport(run.run_id)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-400" />
              Synthesis Report
            </DialogTitle>
            <DialogDescription>
              {selectedReport && formatDate(selectedReport.run_date)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedReport && (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-3">
                <div className="p-3 bg-slate-800 rounded text-center">
                  <p className="text-xl font-bold text-teal-400">{selectedReport.total_patients}</p>
                  <p className="text-xs text-slate-400">Total</p>
                </div>
                <div className="p-3 bg-red-500/10 rounded text-center">
                  <p className="text-xl font-bold text-red-400">{selectedReport.critical_patients?.length || 0}</p>
                  <p className="text-xs text-slate-400">Critical</p>
                </div>
                <div className="p-3 bg-amber-500/10 rounded text-center">
                  <p className="text-xl font-bold text-amber-400">{selectedReport.urgent_patients?.length || 0}</p>
                  <p className="text-xs text-slate-400">Urgent</p>
                </div>
                <div className="p-3 bg-slate-800 rounded text-center">
                  <p className="text-xl font-bold text-purple-400">{selectedReport.poc_changes_proposed}</p>
                  <p className="text-xs text-slate-400">POC Changes</p>
                </div>
              </div>
              
              {/* Critical Patients */}
              {selectedReport.critical_patients?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-red-400 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Critical Patients
                  </h4>
                  <div className="space-y-2">
                    {selectedReport.critical_patients.map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-red-500/10 rounded border border-red-500/30">
                        <span className="text-sm">{p.patient_name}</span>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-red-500/20 text-red-300">PPS {p.pps_score}%</Badge>
                          {p.imminent_death_flagged && (
                            <Badge className="bg-red-600 text-white">ACTIVE DYING</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Urgent Patients */}
              {selectedReport.urgent_patients?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Urgent Patients
                  </h4>
                  <div className="space-y-2">
                    {selectedReport.urgent_patients.slice(0, 5).map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-amber-500/10 rounded border border-amber-500/30">
                        <span className="text-sm">{p.patient_name}</span>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-amber-500/20 text-amber-300">PPS {p.pps_score}%</Badge>
                          <Badge variant="outline" className="text-xs">↓{p.pps_decline}%</Badge>
                        </div>
                      </div>
                    ))}
                    {selectedReport.urgent_patients.length > 5 && (
                      <p className="text-xs text-slate-500 text-center">
                        +{selectedReport.urgent_patients.length - 5} more urgent patients
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AutomatedSynthesisSettings;
